import { toDate } from './date';

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const DATE_ONLY_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// UTC days and months carry no time of day, so they are formatted in UTC to read the same in every timezone.
const UTC_DATE_ONLY_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const UTC_MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const UTC_MONTH_FORMAT = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

const TIME_ONLY_FORMAT = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

const GREETING_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const GREETING_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' });

// "Sunday 2 August · 17:38 CEST" — no explicit timeZone passed, so this reads the
// browser's local timezone (operator's own), not a fixed one.
export const formatGreetingTimestamp = (date: Date = new Date()): string => `${GREETING_DATE_FORMAT.format(date)} · ${GREETING_TIME_FORMAT.format(date)}`;

const EURO_FORMAT = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' });

// Port of legacy helpers/formatDate.js: seconds-vs-ms epoch handling, 'not set' on invalid
export const formatDate = (value: number | string | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'not set';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return 'not set';
  return DATE_FORMAT.format(date);
};

// UTC sibling of formatDate for settings authored in UTC ("2026-10-20 08:00 UTC"), so every operator reads the same time.
export const formatUtcDateTime = (value: number | string | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'not set';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return 'not set';
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

// Browser-locale sibling of formatDate — the questionnaires tab's port of the same
// legacy helpers/formatDate.js rendered via toLocaleDateString/toLocaleTimeString,
// and that operator-locale output is required legacy parity, kept deliberately.
export const formatDateLocale = (value: number | string | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'not set';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return 'not set';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

// Date-only sibling of formatDate ("02 Aug 2026", no time). `fallback` covers the
// callers' divergent conventions ('not set' vs raw-input passthrough).
export const formatDateOnly = (value: number | string | Date | null | undefined, fallback = 'not set'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return DATE_ONLY_FORMAT.format(date);
};

/** UTC sibling of formatDateOnly for a UTC day: "02 Aug 2026", or "01–31 Oct 2026" up to a later `last` day */
export const formatUtcDateOnly = (first: number, last = first): string => (last === first ? UTC_DATE_ONLY_FORMAT.format(first) : UTC_DATE_ONLY_FORMAT.formatRange(first, last));

/** "Oct 2026" for the UTC month `value` falls in, or "Oct" without the year */
export const formatUtcMonth = (value: number, withYear = true): string => (withYear ? UTC_MONTH_YEAR_FORMAT : UTC_MONTH_FORMAT).format(value);

/** "17:01" in the operator's local timezone */
export const formatTimeOnly = (epoch: number): string => TIME_ONLY_FORMAT.format(epoch);

export const formatEuros = (value: number): string => EURO_FORMAT.format(value);

const USD_FORMATS = new Map<number, Intl.NumberFormat>();

export const formatUsd = (value: number, fractionDigits = 2): string => {
  let format = USD_FORMATS.get(fractionDigits);
  if (!format) {
    format = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
    USD_FORMATS.set(fractionDigits, format);
  }
  return format.format(value);
};

const COMPACT_USD_FORMAT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });

/** Short USD for chart axes: $750, $1.5K, $2M. */
export const formatCompactUsd = (value: number): string => COMPACT_USD_FORMAT.format(value);

const PERCENT_FORMAT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });

/** A 0–1 fraction as a percentage with at most one decimal: 0.075 → 7.5%. */
export const formatPercent = (fraction: number): string => PERCENT_FORMAT.format(fraction);

// Per-row currency (treasury pages). Intl throws RangeError on unknown ISO codes —
// legacy had no guard and crashed the row; fall back to a plain suffix instead.
export const formatCurrency = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toLocaleString('en-GB')} ${currency}`;
  }
};

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB'];

// Port of legacy helpers/formatBytes.ts
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** unitIndex;
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${BYTE_UNITS[unitIndex]}`;
};

const TIER_LARGE_MAGNITUDE = 100;
const TIER_UNIT_MAGNITUDE = 1;
const TIER_LARGE_FRACTION_DIGITS = 2;
const TIER_UNIT_FRACTION_DIGITS = 4;
const TIER_SMALL_FRACTION_DIGITS = 8;

/** Magnitude-tiered precision, en-GB grouping (legacy rule: ≥100 → 2 dp, ≥1 → 4 dp, else 8 dp; 0 → '0'). */
export const formatTieredNumber = (value: number): string => {
  if (value === 0) return '0';
  const magnitude = Math.abs(value);
  const maximumFractionDigits =
    magnitude >= TIER_LARGE_MAGNITUDE ? TIER_LARGE_FRACTION_DIGITS : magnitude >= TIER_UNIT_MAGNITUDE ? TIER_UNIT_FRACTION_DIGITS : TIER_SMALL_FRACTION_DIGITS;
  return value.toLocaleString('en-GB', { maximumFractionDigits });
};

// Generic unknown → display string for key/value detail tables: nullish → '',
// objects/arrays → JSON, everything else → String. (pii-history keeps its own
// array-joining variant — the ', ' join is part of the documented BopUserPiiPanel port.)
export const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Port of legacy server/helpers/truncateZeros.ts: strip trailing zeros from the
// fractional part ("12.34500000" → "12.345", "5.000" → "5", "12" → "12")
export const truncateZeros = (value: string | number): string => {
  const text = String(value);
  if (!text.includes('.')) return text;
  const [integer, fraction] = text.split('.');
  const trimmed = fraction.replace(/0+$/, '');
  return trimmed ? `${integer}.${trimmed}` : integer;
};

// Legacy bop-ui friendlyFormatUnixTimestamp port (epoch length sniffing: s/ms/us/ns).
// Used across the user console; falls back to the raw number on unrecognized input.
const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const EPOCH_SECONDS_DIGITS = 10;
const EPOCH_MILLIS_DIGITS = 13;
const EPOCH_MICROS_DIGITS = 16;
const EPOCH_NANOS_DIGITS = 19;

/**
 * Port of legacy server/helpers/friendlyFormatUnixTimestamp.ts: length-based
 * unit sniffing, then `Mar 4, 2024, 09:15` in LOCAL time. User-console copy depends on
 * this exact shape — deliberately distinct from formatDate above.
 */
export const formatFriendlyTimestamp = (epoch: number): string => {
  try {
    let adjustedEpoch = epoch;
    const length = epoch.toString().length;
    if (length === EPOCH_SECONDS_DIGITS) {
      adjustedEpoch = epoch * 1000;
    } else if (length === EPOCH_MILLIS_DIGITS) {
      adjustedEpoch = epoch;
    } else if (length === EPOCH_MICROS_DIGITS) {
      adjustedEpoch = Math.floor(epoch / 1000);
    } else if (length === EPOCH_NANOS_DIGITS) {
      adjustedEpoch = Math.floor(epoch / 1_000_000);
    } else {
      throw new Error('Unrecognized epoch format');
    }

    const date = new Date(adjustedEpoch);
    const month = MONTH_ABBREVIATIONS[date.getMonth()];
    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    return `${month} ${date.getDate()}, ${date.getFullYear()}, ${hours}:${minutes}`;
  } catch {
    return epoch.toString();
  }
};
